import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { SuccessSvg } from "~/ui-rtk/components/ui/svg/essentials"
import {
  CURRENCY_OPTIONS,
  type TCompanySettingsFormValues,
  useConnect,
} from "./connect"
import useLoadingState from "~/ui-rtk/hooks/loading-state"
import { Link } from "react-router-dom"
import { H3 } from "~/ui-rtk/components/ui/typography"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { ErrorMessage, Field, Form, Formik } from "formik"
import { Input, Button } from "~/ui-rtk/components/ui/controls"
import { Divider } from "~/ui-rtk/components/ui/common"
import { Toggle } from "~/ui-rtk/components/ui/controls/Toggle"
import DateField from "~/ui-rtk/components/ui/controls/DateField/DateField"
import type { DateOrNull } from "~/ui-rtk/shared/types/date"
import { useCompanyUserControllerNotifyMutation } from "~/ui-rtk/api/companyUserApi"
import { useCompanyControllerUpgradeToStandardMutation } from "~/ui-rtk/api/companyApi"
import { useAuthControllerCurrentUserQuery } from "~/ui-rtk/api/authApi"
import { CompanyTier } from "~/ui-rtk/api/types"
import { CompanyUserNotification } from "~/api/dto/company-user/company-user-notification"
import { copyTextToClipboard } from "~/ui-rtk/utils/clipboard"
import useGaMultiPropertyAccess from "~/ui-rtk/hooks/ga-multi-property-access"
import useAccess from "~/ui-rtk/hooks/access"
import UtmSettings from "~/ui-rtk/components/features/settings/UtmSettings"
import CompanySyncStateForm from "~/ui-rtk/components/features/settings/CompanySyncStateForm"
import MetaAccessSettings from "~/ui-rtk/components/features/settings/MetaAccessSettings"
import TikTokAdvertiserSettings from "~/ui-rtk/components/features/settings/TikTokAdvertiserSettings"
import CountryBreakoutGaMap from "~/ui-rtk/components/features/settings/CountryBreakoutGaMap"
import ComboboxInput from "~/ui-rtk/components/ui/controls/ComboboxInput"
import { getTimezoneOptions } from "./timezones"

export default function SettingsCompanyPage() {
  const { currentCompany, initialValues, validationSchema, updateSettings } =
    useConnect()

  const [notify, { isLoading: isCompanyUserNotificationLoading }] =
    useCompanyUserControllerNotifyMutation()

  const { isLoading, setIsLoading } = useLoadingState()

  const hasGaMultiPropertyAccess = useGaMultiPropertyAccess()
  const { isSuperAdmin } = useAccess()

  const timezoneOptions = useMemo(() => getTimezoneOptions(), [])

  const handleNotifyCompanyUsers = useCallback(async () => {
    if (!currentCompany) {
      return
    }

    try {
      await notify({
        id: currentCompany.id,
        companyUserNotifyDto: {
          notificationType: CompanyUserNotification.CONNECTION_COMPLETED,
        },
      }).unwrap()
      toast.success("Notification sent successfully!", {
        toastId: "connection-completed-notification-success",
      })
    } catch (error) {
      toast.error("Notification failed", {
        toastId: "connection-completed-notification-error",
      })
    }
  }, [currentCompany, isCompanyUserNotificationLoading])

  const [upgradeToStandard, { isLoading: isUpgradeLoading }] =
    useCompanyControllerUpgradeToStandardMutation()
  const { refetch: refetchCurrentUser, isUninitialized } =
    useAuthControllerCurrentUserQuery()

  const handleUpgradeToStandard = useCallback(async () => {
    if (!currentCompany) {
      return
    }
    if (
      !window.confirm(
        `Upgrade "${currentCompany.name}" from ${currentCompany.tier} to STANDARD? ` +
          "This provisions the Fivetran and Airbyte destinations and cannot be undone from the UI.",
      )
    ) {
      return
    }
    try {
      await upgradeToStandard({ id: currentCompany.id }).unwrap()
      if (!isUninitialized) {
        await refetchCurrentUser()
      }
      toast.success("Company upgraded to STANDARD", {
        toastId: "upgrade-to-standard-success",
      })
    } catch (error) {
      const status = (error as { status?: number })?.status
      toast.error(
        status === 409
          ? "An upgrade is already in progress — retry shortly"
          : "Failed to upgrade company",
        { toastId: "upgrade-to-standard-error" },
      )
    }
  }, [currentCompany, isUninitialized])

  useEffect(() => {
    document.title = "Marathon Data: Settings > Company Settings"
  }, [])

  const handleUpdateSettings = async (values: TCompanySettingsFormValues) => {
    setIsLoading(true)
    await updateSettings(values).catch(() => null)
    setIsLoading(false)
  }

  const [showClipboardSuccess, setShowClipboardSuccess] = useState(false)

  const copyToClipboard = () => {
    void copyTextToClipboard(currentCompany?.id || "")
      .then(() => setShowClipboardSuccess(true))
      .catch(() =>
        toast.error("Failed to copy id to clipboard", {
          toastId: "clipboard-copy-company-id",
        }),
      )
  }

  return (
    <div className="px-6 pt-6">
      <div className="text-start">
        <Breadcrumbs
          items={[
            {
              slug: "/settings",
              name: "Settings",
            },
            {
              slug: "company",
              name: "Company Settings",
            },
          ]}
        />
      </div>
      <div className="space-y-8 mt-4 pr-8 text-start">
        <div className="space-y-2">
          <h3>Notify company members when data is ready</h3>
          <Button
            variant={{ variant: "solid", color: "white" }}
            size="md"
            type="button"
            isLoading={isCompanyUserNotificationLoading || isLoading}
            onClick={handleNotifyCompanyUsers}
          >
            Notify
          </Button>
        </div>
        <Formik
          initialValues={initialValues}
          onSubmit={handleUpdateSettings}
          validationSchema={validationSchema}
        >
          {({ setFieldValue, values }) => (
            <Form className="py-6">
              <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                <h3 className="row-span-2 py-3">Company ID:</h3>
                <div className="relative col-span-2">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-x-4">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => copyToClipboard()}
                      >
                        {currentCompany?.id}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="size-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                          />
                        </svg>
                      </button>
                      {showClipboardSuccess ? (
                        <div className="flex items-center justify-center gap-2 text-sm text-american-green">
                          <SuccessSvg fill="green" size={20} />
                          <p>Successfully copied to the clipboard!</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <Divider className="my-3" />
              <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                <h3 className="row-span-2 py-3">Industry</h3>
                <div className="relative col-span-2">
                  <div className="flex items-center gap-2">
                    <Field
                      type="text"
                      id="industry"
                      name="industry"
                      as={Input}
                      value={values.industry}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                <h3 className="row-span-2 py-3">Website domain</h3>
                <div className="relative col-span-2">
                  <div className="flex items-center gap-2">
                    <Field
                      type="text"
                      id="domain"
                      name="domain"
                      as={Input}
                      placeholder="example.com"
                      value={values.domain}
                    />
                    <ErrorMessage
                      component="a"
                      name="domain"
                      className="absolute input-error-message"
                    />
                  </div>
                  <p className="pt-1 text-xs text-basic-grey">
                    The bare host your ads should land on. The destination audit
                    flags any other host — update it after a site move.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                <h3 className="row-span-2 py-3">Timezone</h3>
                <div className="relative col-span-2">
                  <div className="flex items-center gap-2">
                    <ComboboxInput
                      value={values.timezone || ""}
                      onChange={(value: string) =>
                        setFieldValue("timezone", value)
                      }
                      options={timezoneOptions}
                      placeholder="Select timezone..."
                    />
                    <ErrorMessage
                      component="a"
                      name="timezone"
                      className="absolute input-error-message"
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                <h3 className="row-span-2 py-3">Default Currency</h3>
                <div className="relative col-span-2">
                  <div className="flex items-center gap-2">
                    {isSuperAdmin ? (
                      <Field
                        as="select"
                        id="currency"
                        name="currency"
                        className="select w-full bg-basic-dark-blue border border-basic-blue"
                        value={values.currency || ""}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          setFieldValue("currency", e.target.value)
                        }
                      >
                        <option value="" disabled>
                          Select currency...
                        </option>
                        {CURRENCY_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Field>
                    ) : (
                      <span className="py-3">
                        {CURRENCY_OPTIONS.find(o => o.value === values.currency)
                          ?.label ||
                          values.currency ||
                          "Not set"}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                <h3 className="row-span-2 py-3">Grant free access</h3>
                <div className="relative col-span-2">
                  <div className="flex items-center gap-2">
                    <Field
                      type="text"
                      id="freeAccessEndsAt"
                      name="freeAccessEndsAt"
                      as={DateField}
                      value={values.freeAccessEndsAt}
                      onChange={(date: DateOrNull) =>
                        setFieldValue("freeAccessEndsAt", date)
                      }
                    />
                    <ErrorMessage
                      component="a"
                      name="freeAccessEndsAt"
                      className="absolute input-error-message"
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                <div className="row-span-2 py-3">Data processing Enabled:</div>
                <div className="relative col-span-2">
                  <Field
                    id="isPipelineEnabled"
                    name="isPipelineEnabled"
                    as={Toggle}
                    value={values.isPipelineEnabled}
                    onChange={(value: boolean) =>
                      setFieldValue("isPipelineEnabled", value)
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                <h3 className="row-span-2 py-3">Billing Enabled:</h3>
                <div className="relative col-span-2">
                  <div className="flex items-center gap-2">
                    <Field
                      type="text"
                      id="isBillingEnabled"
                      name="isBillingEnabled"
                      as={Toggle}
                      value={values.isBillingEnabled}
                      onChange={(date: boolean) =>
                        setFieldValue("isBillingEnabled", date)
                      }
                    />
                    <ErrorMessage
                      component="a"
                      name="isBillingEnabled"
                      className="absolute input-error-message"
                    />
                  </div>
                </div>
              </div>
              {isSuperAdmin && (
                <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                  <label htmlFor="isAgencyClient" className="row-span-2 py-3">
                    Agency client:
                  </label>
                  <div className="relative col-span-2">
                    <Field
                      id="isAgencyClient"
                      name="isAgencyClient"
                      as={Toggle}
                      value={values.isAgencyClient}
                      onChange={(value: boolean) =>
                        setFieldValue("isAgencyClient", value)
                      }
                    />
                  </div>
                </div>
              )}
              {hasGaMultiPropertyAccess && (
                <>
                  <Divider className="my-3" />
                  <H3>Flags</H3>
                  <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                    <h3 className="row-span-2 py-3">GA Multi Property:</h3>
                    <div className="relative col-span-2">
                      <div className="flex items-center gap-2">
                        <Field
                          type="text"
                          id="gaMultiProperty"
                          name="gaMultiProperty"
                          as={Toggle}
                          value={values.gaMultiProperty}
                          onChange={(date: boolean) =>
                            setFieldValue("gaMultiProperty", date)
                          }
                        />
                        <ErrorMessage
                          component="a"
                          name="gaMultiProperty"
                          className="absolute input-error-message"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <Divider className="my-3" />
              <H3>Country Breakout (USA / Canada)</H3>
              <p className="py-2 text-sm text-cadet-blue">
                Splits Shopify, GA4 and paid-media KPIs by country in the
                Revenue KPI page&apos;s Regional view. Campaigns are classified
                by a case-insensitive slug in their name, GA4 properties by the
                map below, and Shopify orders by currency, then shipping
                country. Tables fill on the next pipeline run after saving.
              </p>
              <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                <h3 className="row-span-2 py-3">Enabled:</h3>
                <div className="relative col-span-2">
                  <div className="flex items-center gap-2">
                    <Field
                      type="text"
                      id="countryBreakoutEnabled"
                      name="countryBreakoutEnabled"
                      as={Toggle}
                      value={values.countryBreakoutEnabled}
                      onChange={(value: boolean) =>
                        setFieldValue("countryBreakoutEnabled", value)
                      }
                    />
                    <ErrorMessage
                      component="a"
                      name="countryBreakoutEnabled"
                      className="absolute input-error-message"
                    />
                  </div>
                </div>
              </div>
              {values.countryBreakoutEnabled && (
                <>
                  <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                    <h3 className="row-span-2 py-3">USA campaign slug:</h3>
                    <div className="relative col-span-2">
                      <div className="flex items-center gap-2">
                        <Field
                          type="text"
                          id="usaCampaignSlug"
                          name="usaCampaignSlug"
                          as={Input}
                          value={values.usaCampaignSlug}
                          placeholder="e.g. _US"
                        />
                        <ErrorMessage
                          component="a"
                          name="usaCampaignSlug"
                          className="absolute input-error-message"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                    <h3 className="row-span-2 py-3">Canada campaign slug:</h3>
                    <div className="relative col-span-2">
                      <div className="flex items-center gap-2">
                        <Field
                          type="text"
                          id="canadaCampaignSlug"
                          name="canadaCampaignSlug"
                          as={Input}
                          value={values.canadaCampaignSlug}
                          placeholder="e.g. _CA"
                        />
                        <ErrorMessage
                          component="a"
                          name="canadaCampaignSlug"
                          className="absolute input-error-message"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
                    <h3 className="row-span-2 py-3">GA4 property countries:</h3>
                    <div className="relative col-span-2">
                      <CountryBreakoutGaMap
                        value={values.gaPropertyCountries}
                        onChange={next =>
                          setFieldValue("gaPropertyCountries", next)
                        }
                      />
                    </div>
                  </div>
                </>
              )}

              <Divider className="my-3" />
              <div className="items-right flex justify-between items-center">
                <Button
                  variant={{ variant: "solid", color: "pink" }}
                  size="sm"
                  type="submit"
                  isLoading={isLoading}
                >
                  Update
                </Button>
              </div>
            </Form>
          )}
        </Formik>

        {isSuperAdmin && (
          <>
            <Divider className="my-3" />
            <div className="space-y-2">
              <H3>Company Tier</H3>
              <div className="flex items-center gap-4">
                <span>{currentCompany?.tier ?? "Unknown"}</span>
                {(currentCompany?.tier === CompanyTier.SOS_FREE ||
                  currentCompany?.tier === CompanyTier.STALE) && (
                  <Button
                    variant={{ variant: "solid", color: "pink" }}
                    size="sm"
                    type="button"
                    isLoading={isUpgradeLoading}
                    onClick={handleUpgradeToStandard}
                  >
                    Upgrade to STANDARD
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        <Divider className="my-3" />
        {/* Ad naming moved to Setup -> Tags, next to the categories and values
            the convention is built from. Left as a pointer rather than removed
            outright: this is where people have always gone to find it. */}
        <div>
          <H3>Ad Naming</H3>
          <p className="text-sm text-system-gray-400 mt-1">
            The ad-naming convention now lives with the rest of the tag
            taxonomy.{" "}
            <Link
              to={brandRoutes.SETUP.TAGS}
              className="text-system-sea-500 hover:underline"
            >
              Go to Setup &rarr; Tags
            </Link>
          </p>
        </div>

        <Divider className="my-3" />
        <UtmSettings />

        {isSuperAdmin && <MetaAccessSettings />}
        {isSuperAdmin && <TikTokAdvertiserSettings />}
        {isSuperAdmin && <CompanySyncStateForm />}
      </div>
    </div>
  )
}
